/**
 * Data coverage — when each source's collection begins (operator 2026-09-22).
 * A range reaching back before a source's start shows "not collected" on its
 * chart; this table is the reference for why each source starts where it
 * does. "Stored from" can predate "activity from": LinkedIn and Search
 * Console backfill zeros for days before a site or Page existed.
 */
import type { CoverageRow } from '../../lib/analyticsShape.js';

export function CoverageTable({ rows, loading }: { rows: CoverageRow[]; loading?: boolean }) {
  return (
    <section aria-label="Data coverage" className="bg-surface rounded-xl border border-grey-200 p-4 flex flex-col gap-3">
      <header>
        <h2 className="text-sm font-semibold text-grey-900">Data coverage</h2>
        <p className="text-xs text-grey-500 mt-0.5">
          When each source's collection begins. Charts shade the days before it as "not collected"; days
          between the first stored day and the first activity are real zeros, from before the site or Page existed.
        </p>
      </header>
      {loading ? (
        <p className="text-sm text-grey-400 py-4 text-center">Loading…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-grey-500">
                <th className="py-1 pr-3 font-medium">Source</th>
                <th className="py-1 pr-3 font-medium whitespace-nowrap">Stored from</th>
                <th className="py-1 pr-3 font-medium whitespace-nowrap">Activity from</th>
                <th className="py-1 pr-3 font-medium whitespace-nowrap">Through</th>
                <th className="py-1 pr-3 font-medium">Why it starts there</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-grey-100 text-grey-900">
                  <td className="py-1 pr-3 font-medium">{r.label}</td>
                  <td className="py-1 pr-3 tabular-nums whitespace-nowrap">{r.storedFrom ?? '—'}</td>
                  <td className="py-1 pr-3 tabular-nums whitespace-nowrap">
                    {r.activityFrom ?? <span className="text-grey-400">none yet</span>}
                  </td>
                  <td className="py-1 pr-3 tabular-nums whitespace-nowrap text-grey-600">{r.through ?? '—'}</td>
                  <td className="py-1 pr-3 text-grey-500">{r.why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
